<template>
  <div class="canvas-studio-container" ref="containerRef">
    <!-- 顶部悬浮工具栏 -->
    <div class="canvas-toolbar">
      <div class="toolbar-left">
        <label class="custom-file-upload">
          <input
            type="file"
            @change="uploadBaseImage"
            accept="image/*"
            class="hidden-file-input"
          />
          <el-icon><Upload /></el-icon>
          <span>{{ hasImage ? "更换底图" : "上传手绘底图" }}</span>
        </label>
        <span class="toolbar-tip">支持 2:1 比例手绘地图 (JPG/PNG)</span>
      </div>

      <div class="toolbar-right" v-if="hasImage">
        <div class="zoom-indicator">
          缩放: {{ Math.round(imgScale * 100) }}%
        </div>
        <el-tooltip content="重置居中" placement="top">
          <button class="tool-btn" @click="resetTransform">
            <el-icon><RefreshRight /></el-icon>
          </button>
        </el-tooltip>
        <el-tooltip content="放大" placement="top">
          <button class="tool-btn" @click="zoomStep(0.1)">
            <el-icon><ZoomIn /></el-icon>
          </button>
        </el-tooltip>
        <el-tooltip content="缩小" placement="top">
          <button class="tool-btn" @click="zoomStep(-0.1)">
            <el-icon><ZoomOut /></el-icon>
          </button>
        </el-tooltip>
      </div>
    </div>

    <!-- 画布主体视口 -->
    <div
      class="canvas-viewport"
      :class="{ 'is-grabbing': isDragging, 'has-img': hasImage }"
    >
      <canvas
        ref="canvasRef"
        class="main-canvas"
        @mousedown="startDragging"
        @mousemove="dragging"
        @mouseup="stopDragging"
        @mouseleave="stopDragging"
        @wheel="handleWheel"
      ></canvas>

      <!-- 未上传底图时的空态引导 -->
      <div v-if="!hasImage" class="empty-canvas-guide">
        <div class="guide-circle">
          <el-icon><MapLocation /></el-icon>
        </div>
        <h4 class="guide-title">暂未加载手绘地图底图</h4>
        <p class="guide-desc">
          点击左上方按钮上传园区或景区手绘平面图<br />
          上传后支持鼠标滚轮平滑缩放与按住画布拖拽移动
        </p>
      </div>

      <!-- 操作小贴士 -->
      <div v-if="hasImage" class="canvas-hints">
        <span>滚轮缩放 · 拖拽平移</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, computed } from "vue";
import axios from "axios";
import { useStore } from "vuex";
import { ElMessage } from "element-plus";
import { deleteFile } from "@/api/user"; // 或从你的 api 模块引入
import {
  Upload,
  RefreshRight,
  ZoomIn,
  ZoomOut,
  MapLocation,
} from "@element-plus/icons-vue";

export default defineComponent({
  name: "CanvasImageUploader",
  components: {
    Upload,
    RefreshRight,
    ZoomIn,
    ZoomOut,
    MapLocation,
  },
  props: ["imgUrl"],
  setup(props) {
    const containerRef = ref<HTMLDivElement | null>(null);
    const canvasRef = ref<HTMLCanvasElement | null>(null);
    const context = ref<CanvasRenderingContext2D | null>(null);
    const baseImageRef = ref<HTMLImageElement | null>(null);
    const overlayImages = ref<
      Array<{
        image: HTMLImageElement;
        position: { x: number; y: number };
        scale: number;
      }>
    >([]);

    const isDragging = ref(false);
    const dragStart = ref({ x: 0, y: 0 });
    const imgPosition = ref({ x: 0, y: 0 });
    const imgScale = ref(1);
    const baseScale = ref(1);
    const draggingOverlayIndex = ref<number | null>(null);
    const store = useStore();
    const files = ref<File | null>(null);
    const hasImage = computed(() => Boolean(baseImageRef.value));

    // 动态调整 Canvas 分辨率匹配父容器大小
    const resizeCanvas = () => {
      const canvas = canvasRef.value;
      const container = containerRef.value;
      if (!canvas || !container) return;

      const rect = container.getBoundingClientRect();
      // 工具栏高度约 48px
      canvas.width = rect.width || 600;
      canvas.height = rect.height ? rect.height - 48 : 500;

      if (context.value) {
        drawImage();
      }
    };

    onMounted(() => {
      const canvas = canvasRef.value;
      if (canvas) {
        context.value = canvas.getContext("2d");
        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);
      }

      if (props.imgUrl && props.imgUrl !== "false") {
        customeMap2(props.imgUrl);
      }
    });

    onUnmounted(() => {
      window.removeEventListener("resize", resizeCanvas);
    });

    // 计算适应尺寸并居中
    const fitImageToCanvas = (image: HTMLImageElement) => {
      const canvas = canvasRef.value;
      if (!canvas) return;

      const widthRatio = canvas.width / image.width;
      const heightRatio = canvas.height / image.height;
      const scaleRatio = Math.min(widthRatio, heightRatio) * 0.95;

      baseScale.value = scaleRatio;
      imgScale.value = scaleRatio;

      const scaledWidth = image.width * scaleRatio;
      const scaledHeight = image.height * scaleRatio;

      imgPosition.value = {
        x: (canvas.width - scaledWidth) / 2,
        y: (canvas.height - scaledHeight) / 2,
      };
    };

    // 上传底图
    const uploadBaseImage = async (event: Event) => {
      const input = event.target as HTMLInputElement;
      if (!input.files || !input.files[0]) return;

      const file = input.files[0];

      try {
        // 1. 如果存在旧图记录，先调用接口删除远程文件
        // 注：根据你实际保存旧图文件名的字段来取，例如 files.value?.name 或 form.baseImageUrl
        const oldFileName = files.value?.name;
        if (oldFileName) {
          await deleteFile({ fileName: oldFileName });
        }

        // 2. 更新当前本地文件引用
        files.value = file;

        // 3. 载入新图片（推荐使用 createObjectURL，比 FileReader 性能更好且不占内存）
        const image = new Image();
        const objectUrl = URL.createObjectURL(file);
        image.src = objectUrl;

        image.onload = () => {
          // 成功绘制后释放 blob 内存
          URL.revokeObjectURL(objectUrl);

          baseImageRef.value = image;
          fitImageToCanvas(image);
          drawImage();
          ElMessage.success("底图载入成功");
        };

        image.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          ElMessage.error("图片解析失败，请检查文件格式");
        };
      } catch (error) {
        console.error("替换底图失败:", error);
        ElMessage.error("旧图删除或替换失败");
      } finally {
        // 4. 重置 input，防止用户删除后重新选择同名文件时不触发 change 事件
        input.value = "";
      }
    };

    // 绘制棋盘背景（透明感）
    const drawGridBackground = (
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number
    ) => {
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(0, 0, width, height);

      const gridSize = 20;
      ctx.strokeStyle = "#e2e8f0";
      ctx.lineWidth = 0.5;

      ctx.beginPath();
      for (let x = 0; x < width; x += gridSize) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
      }
      for (let y = 0; y < height; y += gridSize) {
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
      }
      ctx.stroke();
    };

    // 重新绘制画布
    const drawImage = () => {
      const canvas = canvasRef.value;
      const ctx = context.value;
      const baseImage = baseImageRef.value;

      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawGridBackground(ctx, canvas.width, canvas.height);

      if (!baseImage) return;

      const scaledBaseWidth = baseImage.width * imgScale.value;
      const scaledBaseHeight = baseImage.height * imgScale.value;

      // 绘制地图底图带微柔和阴影
      ctx.save();
      ctx.shadowColor = "rgba(15, 23, 42, 0.12)";
      ctx.shadowBlur = 16;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 4;

      ctx.drawImage(
        baseImage,
        imgPosition.value.x,
        imgPosition.value.y,
        scaledBaseWidth,
        scaledBaseHeight
      );
      ctx.restore();

      // 绘制覆盖标点
      overlayImages.value.forEach((overlay) => {
        const scaledOverlayWidth =
          overlay.image.width * overlay.scale * imgScale.value;
        const scaledOverlayHeight =
          overlay.image.height * overlay.scale * imgScale.value;

        const maxX = imgPosition.value.x + scaledBaseWidth - scaledOverlayWidth;
        const maxY =
          imgPosition.value.y + scaledBaseHeight - scaledOverlayHeight;
        overlay.position.x = Math.max(
          imgPosition.value.x,
          Math.min(overlay.position.x, maxX)
        );
        overlay.position.y = Math.max(
          imgPosition.value.y,
          Math.min(overlay.position.y, maxY)
        );

        ctx.drawImage(
          overlay.image,
          overlay.position.x,
          overlay.position.y,
          scaledOverlayWidth,
          scaledOverlayHeight
        );
      });
    };

    const isMouseOnOverlayImage = (offsetX: number, offsetY: number) => {
      for (let i = overlayImages.value.length - 1; i >= 0; i--) {
        const overlay = overlayImages.value[i];
        const scaledOverlayWidth =
          overlay.image.width * overlay.scale * imgScale.value;
        const scaledOverlayHeight =
          overlay.image.height * overlay.scale * imgScale.value;

        if (
          offsetX >= overlay.position.x &&
          offsetX <= overlay.position.x + scaledOverlayWidth &&
          offsetY >= overlay.position.y &&
          offsetY <= overlay.position.y + scaledOverlayHeight
        ) {
          return i;
        }
      }
      return null;
    };

    const startDragging = (event: MouseEvent) => {
      if (!baseImageRef.value) return;
      const { offsetX, offsetY } = event;
      draggingOverlayIndex.value = isMouseOnOverlayImage(offsetX, offsetY);
      isDragging.value = true;
      dragStart.value = { x: event.clientX, y: event.clientY };
    };

    const dragging = (event: MouseEvent) => {
      if (!isDragging.value) return;

      const dx = event.clientX - dragStart.value.x;
      const dy = event.clientY - dragStart.value.y;

      if (draggingOverlayIndex.value !== null) {
        const overlay = overlayImages.value[draggingOverlayIndex.value];
        overlay.position.x += dx;
        overlay.position.y += dy;
      } else {
        imgPosition.value.x += dx;
        imgPosition.value.y += dy;

        overlayImages.value.forEach((overlay) => {
          overlay.position.x += dx;
          overlay.position.y += dy;
        });
      }

      dragStart.value = { x: event.clientX, y: event.clientY };
      drawImage();
    };

    const stopDragging = () => {
      isDragging.value = false;
      draggingOverlayIndex.value = null;
    };

    const handleWheel = (event: WheelEvent) => {
      if (!baseImageRef.value) return;
      event.preventDefault();

      const { offsetX, offsetY } = event;
      const overlayIndex = isMouseOnOverlayImage(offsetX, offsetY);
      const scaleDelta = event.deltaY > 0 ? -0.05 : 0.05;
      const previousScale = imgScale.value;
      const targetScale = Math.max(
        0.05,
        Math.min(5, imgScale.value + scaleDelta)
      );

      if (overlayIndex !== null) {
        const overlay = overlayImages.value[overlayIndex];
        if (overlay) {
          overlay.scale = Math.max(0.1, overlay.scale + scaleDelta);
          drawImage();
        }
      } else {
        imgScale.value = targetScale;
        const centerX = offsetX - imgPosition.value.x;
        const centerY = offsetY - imgPosition.value.y;

        overlayImages.value.forEach((overlay) => {
          const dx = overlay.position.x - imgPosition.value.x;
          const dy = overlay.position.y - imgPosition.value.y;
          overlay.position.x =
            imgPosition.value.x + (dx * imgScale.value) / previousScale;
          overlay.position.y =
            imgPosition.value.y + (dy * imgScale.value) / previousScale;
        });

        imgPosition.value.x =
          offsetX - centerX * (imgScale.value / previousScale);
        imgPosition.value.y =
          offsetY - centerY * (imgScale.value / previousScale);
        drawImage();
      }
    };

    const zoomStep = (delta: number) => {
      if (!baseImageRef.value || !canvasRef.value) return;
      const canvas = canvasRef.value;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      const fakeWheelEvent = {
        preventDefault: () => {},
        deltaY: delta > 0 ? -1 : 1,
        offsetX: centerX,
        offsetY: centerY,
      } as WheelEvent;

      handleWheel(fakeWheelEvent);
    };

    const resetTransform = () => {
      if (baseImageRef.value) {
        fitImageToCanvas(baseImageRef.value);
        drawImage();
      }
    };

    const customeMap2 = (imgUrl: string) => {
      const image = new Image();
      image.src = imgUrl;

      image.onload = () => {
        baseImageRef.value = image;
        fitImageToCanvas(image);
        drawImage();
      };
    };

    const saveImage = (gameId: string) => {
      if (files.value != null) {
        const form = new FormData();
        form.append("file", files.value);

        const options = {
          method: "POST",
          url: process.env.VUE_APP_Update_API,
          headers: { "content-type": "multipart/form-data" },
          data: form,
        };

        axios
          .request(options)
          .then((response) => {
            if (response.data.code == 200) {
              const payload = {
                gameId: gameId,
                customizeMap: response.data.data.returnFileUrl,
                mapSelect: "自定义地图",
              };
              store.dispatch("game/UpdateGameMap", payload).then(() => {
                ElMessage.success("自定义地图已保存更新");
              });
            }
          })
          .catch((error) => {
            console.error(error);
            ElMessage.error("地图保存失败，请重试");
          });
      } else if (props.imgUrl && props.imgUrl !== "false") {
        const payload = {
          gameId: gameId,
          customizeMap: props.imgUrl,
          mapSelect: "自定义地图",
        };
        store.dispatch("game/UpdateGameMap", payload).then(() => {
          ElMessage.success("自定义地图设置已确认");
        });
      } else {
        ElMessage.warning("请先上传手绘地图底图");
      }
    };

    return {
      containerRef,
      canvasRef,
      hasImage,
      imgScale,
      isDragging,
      uploadBaseImage,
      startDragging,
      dragging,
      stopDragging,
      handleWheel,
      zoomStep,
      resetTransform,
      saveImage,
      customeMap2,
    };
  },
});
</script>

<style scoped lang="less">
@primary-color: #5d5fef;
@primary-hover: #4b4de0;
@primary-light: #f0f2ff;
@text-main: #1e293b;
@text-sub: #64748b;
@text-muted: #94a3b8;
@border-color: #e2e8f0;

.canvas-studio-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
}

// 顶部悬浮工具栏
.canvas-toolbar {
  height: 48px;
  padding: 0 16px;
  background: #ffffff;
  border-bottom: 1px solid @border-color;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  z-index: 5;

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .custom-file-upload {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 14px;
      border-radius: 6px;
      background: @primary-light;
      color: @primary-color;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      border: 1px solid #dce2ff;
      transition: all 0.2s;

      &:hover {
        background: @primary-color;
        border-color: @primary-color;
        color: #ffffff;
      }

      .hidden-file-input {
        display: none;
      }
    }

    .toolbar-tip {
      font-size: 12px;
      color: @text-muted;

      @media (max-width: 640px) {
        display: none;
      }
    }
  }

  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 8px;

    .zoom-indicator {
      font-size: 12px;
      font-weight: 600;
      color: @text-sub;
      padding: 0 8px;
    }

    .tool-btn {
      width: 32px;
      height: 32px;
      border-radius: 6px;
      border: 1px solid @border-color;
      background: #ffffff;
      color: @text-sub;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: @primary-color;
        color: @primary-color;
        background: @primary-light;
      }
    }
  }
}

// 画布视口
.canvas-viewport {
  flex: 1;
  width: 100%;
  position: relative;
  overflow: hidden;
  background: #f8fafc;
  cursor: default;

  &.has-img {
    cursor: grab;
  }

  &.is-grabbing {
    cursor: grabbing !important;
  }

  .main-canvas {
    display: block;
    width: 100%;
    height: 100%;
  }

  // 空态引导
  .empty-canvas-guide {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    padding: 24px;
    text-align: center;

    .guide-circle {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: @primary-light;
      color: @primary-color;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 30px;
      margin-bottom: 14px;
      box-shadow: 0 4px 16px rgba(93, 95, 239, 0.15);
    }

    .guide-title {
      font-size: 16px;
      font-weight: 700;
      color: @text-main;
      margin: 0 0 8px 0;
    }

    .guide-desc {
      font-size: 13px;
      color: @text-muted;
      line-height: 1.6;
      margin: 0;
    }
  }

  // 快捷操作提示
  .canvas-hints {
    position: absolute;
    bottom: 12px;
    right: 12px;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(4px);
    color: #ffffff;
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 6px;
    pointer-events: none;
    letter-spacing: 0.5px;
  }
}
</style>
